import React, { useState } from 'react';
import {BrowserRouter as Router, Routes, Route} from "react-router-dom";
import { Auth0Provider } from '@auth0/auth0-react';
import { CardProvider } from './common/CardContext';
import { ChatUIPersistenceProvider } from './common/ChatUIPersistenceContext';
import { ThemeProvider } from './common/ThemeContext';
import MqttListener from './mqtt/MqttListener';
import { ActuatorPresenceProvider } from './actions/ActuatorPresence';
import { RiQuestionLine } from "react-icons/ri";

import Header from "./layout/Header";
import Dashboard from "./cards/Dashboard";
import Sidebar from "./layout/Sidebar";
import ActionWrapper from "./actions/ActionWrapper";
import QuickActionsPage from "./actions/QuickActionsPage";
import CameraWrapper from "./camera/CameraWrapper";
import Settings from "./common/Settings";
import DeviceHistory from "./common/DeviceHistory";
import LawnDashboard from "./lawn/LawnDashboard";
import About from "./common/About";
import Logout from "./auth/Logout";
import Login from "./auth/Login";
import Callback from "./auth/Callback";
import PrivateRoute from "./auth/PrivateRoute";
import MqttWrapper from "./mqtt/MqttWrapper";
import DiscreteSensorWrapper from "./cards/DiscreteSensorWrapper";
import CardModal from "./cards/CardModal";

// Public page components
import HomePage from "./public/HomePage";
import DemoPage from "./public/DemoPage";
import NewsPage from "./public/NewsPage";
import ContactPage from "./public/ContactPage";
import PartsListPage from "./public/PartsListPage";
import AutumnPage from "./public/AutumnPage";
import AutumnGuidePage from "./public/AutumnGuidePage";
import Navbar from "./public/Navbar";

// Demo components for testing
import ThresholdTestDemo from "./actions/ThresholdTestDemo";
import DistanceThresholdTest from "./actions/DistanceThresholdTest";

//Import environment variables from .env
/* global process */
const domain = process.env.REACT_APP_AUTH0_DOMAIN;
const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;

export default function App() {
    //Instead of setting useState to True, we'll pull from localStorage
    const [sidebarOpen, setSidebarOpen] = useState(() => {
        return localStorage.getItem('isSidebarOpen') === 'true';
    });

    const [modalOpen, setModalOpen] = useState(false);
    const [tourRunning, setTourRunning] = useState(false);

    const toggleSidebar = () => {
        setSidebarOpen((prevState) => {
            const newState = !prevState;
            localStorage.setItem('isSidebarOpen', newState);
            return newState;
        });
    }

    const openModal = () => setModalOpen(true);
    const closeModal = () => setModalOpen(false);
    
    const startTour = () => setTourRunning(true);
    const handleTourComplete = () => setTourRunning(false);
    const handleTourSkip = () => setTourRunning(false);

    return (
        <Router>
            <Auth0Provider
                domain={domain}
                clientId={clientId}
                authorizationParams= {{ redirect_uri: window.location.origin + '/callback' }}
            >
                <ThemeProvider>
                    <CardProvider>
                        <ChatUIPersistenceProvider>
                        <MqttListener />
                        <ActuatorPresenceProvider>
                    <div className="App">
                        <Routes>
                            {/* Public Routes - No authentication required */}
                            <Route path="/" element={<HomePage />} />
                            <Route path="/demo" element={<DemoPage />} />
                            <Route path="/news" element={<NewsPage />} />
                            <Route path="/contact" element={<ContactPage />} />
                            <Route path="/parts" element={<PartsListPage />} />
                            <Route path="/autumn" element={<AutumnPage />} />
                            <Route path="/autumn/:slug" element={<AutumnGuidePage />} />
                            <Route path="/threshold-test" element={<ThresholdTestDemo />} />
                            <Route path="/distance-test" element={<DistanceThresholdTest />} />
                            <Route path="/login" element={
                                <div>
                                    <Navbar />
                                    <Login />
                                </div>
                            }/>
                            
                            {/* Protected Routes - Authentication required */}
                            <Route path="/dashboard" element={
                                <PrivateRoute>
                                    <div className="App">
                                        <Header />
                                        {/* Button container for help and add buttons - positioned right below header */}
                                        <div className="dashboard-buttons-container">
                                            <div className="dashboard-buttons-wrapper">
                                                <div className="dashboard-buttons-left">
                                                    {/* Help tour button */}
                                                    <button 
                                                        className="help-tour-button" 
                                                        onClick={startTour}
                                                        aria-label="Start Dashboard Tour"
                                                        title="Take a guided tour of the dashboard"
                                                    >
                                                        <RiQuestionLine className="help-icon" />
                                                    </button>
                                                </div>
                                                <h1 className="header-normal dashboard-title">Dashboard</h1>
                                                <div className="dashboard-buttons-right">
                                                    {/* Add card button */}
                                                    <button className="add-card-button green-button" onClick={openModal}>
                                                        <i className="ri-add-line"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="App-content">
                                            <div className={`sidebar-container ${sidebarOpen ? '' : 'sidebar-close'}`}>
                                                <Sidebar />
                                            </div>
                                                <Dashboard 
                                                    tourRunning={tourRunning}
                                                    onTourComplete={handleTourComplete}
                                                    onTourSkip={handleTourSkip}
                                                />
                                            
                                            {/* Modal for adding cards */}
                                            {modalOpen && <CardModal closeModal={closeModal} />}
                                            
                                            {/* Sidebar toggle button - only show for authenticated users */}
                                            <button className="toggle-sidebar-button" onClick={toggleSidebar}>
                                                {sidebarOpen ? <i className="ri-skip-left-line custom-close-icon"></i> : <i className="ri-skip-right-line custom-close-icon"></i>}
                                            </button>
                                        </div>
                                    </div>
                                </PrivateRoute>
                            } />
                            <Route path="/settings" element={
                                <PrivateRoute>
                                    <div className="App">
                                        <Header />
                                        <div className="App-content">
                                            <div className={`sidebar-container ${sidebarOpen ? '' : 'sidebar-close'}`}>
                                                <Sidebar />
                                            </div>
                                                <Settings />
                                            
                                            {/* Sidebar toggle button */}
                                            <button className="toggle-sidebar-button" onClick={toggleSidebar}>
                                                {sidebarOpen ? <i className="ri-skip-left-line custom-close-icon"></i> : <i className="ri-skip-right-line custom-close-icon"></i>}
                                            </button>
                                        </div>
                                    </div>
                                </PrivateRoute>
                            } />
                            {/* Lawn irrigation dashboard prototype — isolated from the sensor dashboard */}
                            <Route path="/lawn" element={
                                <PrivateRoute>
                                    <div className="App">
                                        <Header />
                                        <div className="App-content">
                                            <div className={`sidebar-container ${sidebarOpen ? '' : 'sidebar-close'}`}>
                                                <Sidebar />
                                            </div>
                                                <LawnDashboard />

                                            <button className="toggle-sidebar-button" onClick={toggleSidebar}>
                                                {sidebarOpen ? <i className="ri-skip-left-line custom-close-icon"></i> : <i className="ri-skip-right-line custom-close-icon"></i>}
                                            </button>
                                        </div>
                                    </div>
                                </PrivateRoute>
                            } />
                            <Route path="/devices/history" element={
                                <PrivateRoute>
                                    <div className="App">
                                        <Header />
                                        <div className="App-content">
                                            <div className={`sidebar-container ${sidebarOpen ? '' : 'sidebar-close'}`}>
                                                <Sidebar />
                                            </div>
                                                <DeviceHistory />
                                            
                                            <button className="toggle-sidebar-button" onClick={toggleSidebar}>
                                                {sidebarOpen ? <i className="ri-skip-left-line custom-close-icon"></i> : <i className="ri-skip-right-line custom-close-icon"></i>}
                                            </button>
                                        </div>
                                    </div>
                                </PrivateRoute>
                            } />
                            <Route path="/about" element={
                                <PrivateRoute>
                                    <div className="App">
                                        <Header />
                                        <div className="App-content">
                                            <div className={`sidebar-container ${sidebarOpen ? '' : 'sidebar-close'}`}>
                                                <Sidebar />
                                            </div>
                                                <About />
                                            
                                            {/* Sidebar toggle button */}
                                            <button className="toggle-sidebar-button" onClick={toggleSidebar}>
                                                {sidebarOpen ? <i className="ri-skip-left-line custom-close-icon"></i> : <i className="ri-skip-right-line custom-close-icon"></i>}
                                            </button>
                                        </div>
                                    </div>
                                </PrivateRoute>
                            } />
                            <Route path="/actions/:macAddress" element={
                                <PrivateRoute>
                                    <div className="App">
                                        <Header />
                                        <div className="App-content">
                                            <div className={`sidebar-container ${sidebarOpen ? '' : 'sidebar-close'}`}>
                                                <Sidebar />
                                            </div>
                                                <ActionWrapper />
                                            
                                            {/* Sidebar toggle button */}
                                            <button className="toggle-sidebar-button" onClick={toggleSidebar}>
                                                {sidebarOpen ? <i className="ri-skip-left-line custom-close-icon"></i> : <i className="ri-skip-right-line custom-close-icon"></i>}
                                            </button>
                                        </div>
                                    </div>
                                </PrivateRoute>
                            } />
                            <Route path="/quick-actions" element={
                                <PrivateRoute>
                                    <div className="App">
                                        <Header />
                                        <div className="App-content">
                                            <div className={`sidebar-container ${sidebarOpen ? '' : 'sidebar-close'}`}>
                                                <Sidebar />
                                            </div>
                                                <QuickActionsPage />
                                            
                                            {/* Sidebar toggle button */}
                                            <button className="toggle-sidebar-button" onClick={toggleSidebar}>
                                                {sidebarOpen ? <i className="ri-skip-left-line custom-close-icon"></i> : <i className="ri-skip-right-line custom-close-icon"></i>}
                                            </button>
                                        </div>
                                    </div>
                                </PrivateRoute>
                            } />
                            <Route path="/camera/:macAddress" element={
                                <PrivateRoute>
                                    <div className="App">
                                        <Header />
                                        <div className="App-content">
                                            <div className={`sidebar-container ${sidebarOpen ? '' : 'sidebar-close'}`}>
                                                <Sidebar />
                                            </div>
                                                <CameraWrapper />
                                            
                                            {/* Sidebar toggle button */}
                                            <button className="toggle-sidebar-button" onClick={toggleSidebar}>
                                                {sidebarOpen ? <i className="ri-skip-left-line custom-close-icon"></i> : <i className="ri-skip-right-line custom-close-icon"></i>}
                                            </button>
                                        </div>
                                    </div>
                                </PrivateRoute>
                            } />
                            <Route path="/logout" element={<Logout />}/>
                            <Route path="/callback" element={<Callback />}/>
                            <Route path="/charts/mqtt/:macAddress" element={
                                <PrivateRoute>
                                    <div className="App">
                                        <Header />
                                        <div className="App-content">
                                            <div className={`sidebar-container ${sidebarOpen ? '' : 'sidebar-close'}`}>
                                                <Sidebar />
                                            </div>
                                                <MqttWrapper />
                                            
                                            {/* Sidebar toggle button */}
                                            <button className="toggle-sidebar-button" onClick={toggleSidebar}>
                                                {sidebarOpen ? <i className="ri-skip-left-line custom-close-icon"></i> : <i className="ri-skip-right-line custom-close-icon"></i>}
                                            </button>
                                        </div>
                                    </div>
                                </PrivateRoute>
                            } />
                            <Route path="/charts/discrete/:macAddress" element={
                                <PrivateRoute>
                                    <div className="App">
                                        <Header />
                                        <div className="App-content">
                                            <div className={`sidebar-container ${sidebarOpen ? '' : 'sidebar-close'}`}>
                                                <Sidebar />
                                            </div>
                                                <DiscreteSensorWrapper />
                                            
                                            {/* Sidebar toggle button */}
                                            <button className="toggle-sidebar-button" onClick={toggleSidebar}>
                                                {sidebarOpen ? <i className="ri-skip-left-line custom-close-icon"></i> : <i className="ri-skip-right-line custom-close-icon"></i>}
                                            </button>
                                        </div>
                                    </div>
                                </PrivateRoute>
                            } />
                        </Routes>
                    </div>
                        </ActuatorPresenceProvider>
                    </ChatUIPersistenceProvider>
                    </CardProvider>
                </ThemeProvider>
            </Auth0Provider>
        </Router>
    );
}